import { useEffect, useState } from "react";
import { Button, Grid, Typography, Divider } from "@material-ui/core";
import { API_ENV } from "constants/environment";
import { useWebsocketFetch } from "hooks/useWebsocketFetch";
import { useVisionLogin } from "hooks/useVisionLogin";
import { IsMobile } from "utils/is-mobile";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import InputText from "components/_refactor/text/input";
import ChooseInvoiceDialog from "../components/choose-invoice-dialog";

export default function InvoiceChoose({
  fieldsData,
  loadingFields,
  isLoading,
  setIsLoading,
  addedProductsList,
  setAddedProductsList,
  clearFilters,
  motivoRequerEvidencia,
}) {
  const [invoice, setInvoice] = useState("");
  const [invoiceSelected, setInvoiceSelected] = useState(null);
  const [isInvoiceValid, setIsInvoiceValid] = useState();
  const [isInvoiceAlreadySelected, setIsInvoiceAlreadySelected] =
    useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchingInvoice, setSearchingInvoice] = useState("");

  const { userId } = useVisionLogin();
  const snackbar = useSnackbar();
  const isMobile = IsMobile();

  const { handleFetchData, status, handleFetchUrl } = useWebsocketFetch({
    onSuccess: (data) => {
      if (!data || !Array.isArray(data) || data.length === 0) {
        setIsInvoiceValid(false);
        setSearchingInvoice("");
        return;
      }

      const invoiceData = data[0];

      if (
        !invoiceData ||
        invoiceData.MessageResponse === "Nenhum registro localizado." ||
        invoiceData.MessageResponse === "Falha em validação de filtros!"
      ) {
        setIsInvoiceValid(false);
        setSearchingInvoice("");
        return;
      }

      if (
        !invoiceData.ListaProdutosExport ||
        !Array.isArray(invoiceData.ListaProdutosExport) ||
        invoiceData.ListaProdutosExport.length === 0
      ) {
        setIsInvoiceValid(false);
        snackbar.showMessage("Nota fiscal sem produtos disponíveis", {
          severity: "error",
        });
        setSearchingInvoice("");
        return;
      }

      const invoiceObj = {
        NumeroNf: invoiceData.NumeroNf || null,
        ValorTotal: invoiceData.ValorTotal || null,
        SerieNf: invoiceData.SerieNf || null,
        DataEmissaoNf: invoiceData.DataEmissaoNf || null,
        items: invoiceData.ListaProdutosExport.map((item) => ({
          selected: false,
          idTableProduct: invoiceData.IdPedido || null,
          product: item,
          name: item.Descricao || null,
          idProduto: item.Codigo || null,
          nameMotivo: undefined,
          nameDefeito: undefined,
          quantity: item.QtdFaturada - item.QuantidadeDevolvida || null,
          motivo: undefined,
          defeito: undefined,
          IdPedido: invoiceData.IdPedido || null,
          QtdFaturada: item.QtdFaturada || null,
          QuantidadeDevolvida: item.QuantidadeDevolvida || 0,
          ValorUnitario: item.ValorUnitario || null,
          ValorTotal:
            (item.QtdFaturada * item.ValorUnitario).toFixed(2) || null,
        })),
      };

      const invoiceNumberToRestore =
        searchingInvoice || invoiceData.NumeroNf?.toString() || invoice;

      setInvoiceSelected(invoiceObj);
      setIsInvoiceValid(true);
      setOpenDialog(true);

      if (!invoice && invoiceNumberToRestore) {
        setInvoice(invoiceNumberToRestore);
      }

      setSearchingInvoice("");
    },
    onError: (error) => {
      snackbar.showMessage(error, {
        severity: "error",
      });
      setIsInvoiceValid(false);
      setSearchingInvoice("");
    },
  });

  const handleSearchInvoice = async () => {
    const invoiceValue = invoice.trim();

    if (!invoiceValue) {
      return;
    }

    if (
      addedProductsList.some((product) => product.NumeroNf === invoiceValue)
    ) {
      setIsInvoiceAlreadySelected(true);
      return;
    }

    setIsInvoiceAlreadySelected(false);
    setIsInvoiceValid(undefined);
    setSearchingInvoice(invoiceValue);

    handleFetchData({
      action: "list-reception-invoices",
      params: {
        querystring: {
          simples: "true",
          idLoja: fieldsData?.idPessoa,
          idNF: invoiceValue,
        },
        header: {
          "K-IdUser": userId,
        },
      },
    });
  };

  useEffect(() => {
    handleFetchUrl(
      `wss://3zvsu3yj2b.execute-api.us-east-1.amazonaws.com/${API_ENV}`
    );
  }, [handleFetchUrl]);

  useEffect(() => {
    if (status === "fetching") {
      setIsLoading(true);
    } else if (status === "off" || status === "empty") {
      setIsLoading(false);

      if (status === "empty" && !openDialog) {
        setIsInvoiceValid(false);
      }
    }
  }, [status, setIsLoading, openDialog]);

  useEffect(() => {
    if (clearFilters && status !== "fetching" && !openDialog) {
      setInvoice("");
      setInvoiceSelected(null);
      setIsInvoiceValid(undefined);
      setIsInvoiceAlreadySelected(false);
      setOpenDialog(false);
      setSearchingInvoice("");
    }
  }, [clearFilters, status, openDialog]);

  useEffect(() => {
    if (
      searchingInvoice &&
      !invoice &&
      (status === "fetching" || status === "off")
    ) {
      setInvoice(searchingInvoice);
    }
  }, [searchingInvoice, invoice, status]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InputText
            label="Nota Fiscal"
            disabled={loadingFields.length > 0}
            value={invoice}
            onChange={(e) => {
              const value = e.target.value
                .toString()
                .replace(/\D/g, "")
                .slice(0, 10);
              setInvoice(value);
              setIsInvoiceValid(undefined);
              setIsInvoiceAlreadySelected(false);
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Button
            style={{
              width: isMobile ? "100%" : "17vw",
              marginBottom: isMobile ? "0.75rem" : "0px",
              marginTop: isMobile ? "0.75rem" : "20px",
              height: "38px",
              alignSelf: "center",
              padding: "0",
            }}
            variant="contained"
            onClick={handleSearchInvoice}
            disabled={!invoice || loadingFields.length > 0 || isLoading}
          >
            {isLoading ? "Pesquisando..." : "Pesquisar"}
          </Button>
        </Grid>

        {isInvoiceAlreadySelected && (
          <Typography
            variant="h5"
            component="h5"
            style={{ color: "grey", marginTop: "1rem" }}
          >
            <i
              className="fa fa-exclamation-circle"
              aria-hidden="true"
              style={{
                fontSize: "1rem",
                marginRight: "0.5rem",
              }}
            />{" "}
            Nota fiscal já selecionada.
          </Typography>
        )}

        {isInvoiceValid === false && (
          <Typography
            variant="h5"
            component="h5"
            style={{ color: "grey", marginTop: "1rem" }}
          >
            <i
              className="fa fa-exclamation-circle"
              aria-hidden="true"
              style={{
                fontSize: "1rem",
                marginRight: "0.5rem",
              }}
            />{" "}
            Nota informado não encontrada.
          </Typography>
        )}
      </Grid>

      <Divider variant="fullWidth" style={{ margin: "20px 0px" }} />

      {openDialog && invoiceSelected && (
        <ChooseInvoiceDialog
          onClose={() => {
            setOpenDialog(false);
            setInvoice("");
            setInvoiceSelected(null);
          }}
          invoice={invoiceSelected}
          selectedProductsList={addedProductsList}
          setSelectedProductsList={setAddedProductsList}
          motivoRequerEvidencia={motivoRequerEvidencia}
        />
      )}
    </>
  );
}
